import React from 'react';
import { Autocomplete, Box, TextField, Typography, createFilterOptions } from '@mui/material';

// Bound the number of mounted menu items for large address books. The source
// options are never truncated, so searching still finds any existing address.
const filterOptions = createFilterOptions({
  stringify: (option) => [
    option.name, option.label, option.street, option.streetAddress,
    option.city, option.state, option.stateProvince, option.postal,
    option.postalZipCode, option.country
  ].filter(Boolean).join(' '),
  limit: 75,
  ignoreCase: true,
  ignoreAccents: true,
});

const SearchableAddressDropdown = ({
  options = [], selectedValue, onSelect, label, placeholder, name,
}) => {
  const normalizedOptions = React.useMemo(() => (
    Array.isArray(options) ? options.filter((option) => option && option.id != null) : []
  ), [options]);

  const selected = React.useMemo(() => (
    normalizedOptions.find((option) => String(option.id) === String(selectedValue)) || null
  ), [normalizedOptions, selectedValue]);

  return (
    <Box sx={{ width: '100%', minWidth: 150 }}>
      <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', color: '#555', mb: 0 }}>
        {label}
      </Typography>
      <Autocomplete
        fullWidth
        size="small"
        options={normalizedOptions}
        value={selected}
        onChange={(_event, option) => onSelect(option?.id ?? '')}
        getOptionLabel={(option) => String(option?.name || option?.label || '')}
        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
        filterOptions={filterOptions}
        autoHighlight
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        ListboxProps={{ style: { maxHeight: 320 } }}
        noOptionsText="No matching addresses"
        renderOption={(props, option) => (
          <li {...props} key={String(option.id)}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>{option.name || option.label}</Typography>
              {[option.street || option.streetAddress, option.city,
                option.state || option.stateProvince, option.postal || option.postalZipCode]
                .filter(Boolean).length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                  {[option.street || option.streetAddress, option.city,
                    option.state || option.stateProvince, option.postal || option.postalZipCode]
                    .filter(Boolean).join(', ')}
                </Typography>
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            placeholder={placeholder}
            variant="outlined"
            inputProps={{ ...params.inputProps, 'aria-label': label }}
            sx={{ backgroundColor: '#fff' }}
          />
        )}
      />
    </Box>
  );
};

export default SearchableAddressDropdown;
